import React, { useId } from 'react';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import { InfoTooltip } from '@/components/InfoTooltip';

/** HTML elements a <label for> can name; any component child is assumed to render one. */
const LABELABLE_ELEMENTS = new Set(['input', 'textarea', 'select', 'button']);

interface FormFieldProps {
  id?: string;
  label?: React.ReactNode;
  helpText?: React.ReactNode;
  /** Explanation shown in an info tooltip beside the label. Prefer this to helpText in multi-column rows, where text under one input pushes the row out of line. */
  info?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;
  children: React.ReactNode;
  className?: string;
}

/**
 * Label / control / help / error wrapper for native form fields. With a single child control, it
 * gives the control an id (`id`, else the control's own, else a generated one) so the label names
 * it, plus `aria-invalid`, `aria-describedby` and `aria-required`. A `Select` passes them on to its
 * `SelectTrigger`. With useForm, spread `form.field(name)` for the id and error.
 */
export function FormField({
  id,
  label,
  helpText,
  info,
  error,
  required,
  children,
  className,
}: FormFieldProps) {
  const autoId = useId();
  // One control to name; a Fragment can't take an id.
  const child =
    React.isValidElement<{ id?: string }>(children) && children.type !== React.Fragment ? children : null;
  // Without an id the label named nothing (T-224): fall back to the control's own id, then a
  // generated one, so the label is tied to its control. Only for a control: a wrapper div or span
  // can't be labelled, so the controls inside one need their own aria-label.
  const labelable =
    child != null && (typeof child.type !== 'string' || LABELABLE_ELEMENTS.has(child.type));
  const fieldId = id ?? child?.props.id ?? (labelable ? autoId : undefined);
  const describedBy = [
    helpText && fieldId ? `${fieldId}-help` : null,
    error && fieldId ? `${fieldId}-error` : null,
  ]
    .filter(Boolean)
    .join(' ') || undefined;

  return (
    // In a grid row a field always sits at the top, so when one field grows (an error, a long
    // label) the labels and inputs beside it stay in line. Only in a grid: in a flex column,
    // self-start would shrink the field's width instead.
    <div className={cn('grid content-start gap-2 [.grid>&]:self-start', className)}>
      {label ? (
        <Label htmlFor={fieldId}>
          {label}
          {required ? (
            <span className="text-destructive" aria-hidden>
              *
            </span>
          ) : null}
          {info ? (
            <InfoTooltip label={typeof label === 'string' ? label : undefined}>{info}</InfoTooltip>
          ) : null}
        </Label>
      ) : null}
      <div
        className={cn(error && '[&_[data-slot=input]]:border-destructive')}
        // Pass through aria-describedby via cloning when child is a single element with id
      >
        {child && fieldId
          ? React.cloneElement(
              child as React.ReactElement<{
                id?: string;
                'aria-invalid'?: boolean;
                'aria-describedby'?: string;
                'aria-required'?: boolean;
              }>,
              {
                id: fieldId,
                'aria-invalid': Boolean(error) || undefined,
                'aria-describedby': describedBy,
                // The asterisk is hidden from screen readers; this says it instead.
                'aria-required': required || undefined,
              }
            )
          : children}
      </div>
      {helpText && !error ? (
        <p id={fieldId ? `${fieldId}-help` : undefined} className="text-xs text-muted-foreground">
          {helpText}
        </p>
      ) : null}
      {error ? (
        <p
          id={fieldId ? `${fieldId}-error` : undefined}
          className="text-xs text-destructive"
          role="alert"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * A button (Save, Send invite) at the end of a row of FormFields. It leaves a label-height gap
 * above, so the button lines up with the inputs while the row stays top-aligned.
 */
export function FormActions({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('grid content-start gap-2 [.grid>&]:self-start', className)}>
      <span aria-hidden className="hidden h-3.5 sm:block" />
      <div className="flex flex-wrap items-center gap-2">{children}</div>
    </div>
  );
}
